import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Phone, Mail, Navigation, CheckCircle2 } from "lucide-react";
import { useTranslation } from "@/i18n";

const field =
  "w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-sm text-[#FAF8F5] placeholder:text-[#9BA1B0] transition-colors duration-300";

export default function Contact() {
  const { t } = useTranslation();

  const info = [
    { icon: MapPin, label: t("contact.addressLabel"), value: t("contact.addressValue"), id: "adresse" },
    { icon: Phone, label: t("contact.phoneLabel"), value: "02/880.64.02", id: "telephone" },
    { icon: Mail, label: t("contact.emailLabel"), value: "info@e2.law", id: "email" },
    { icon: Navigation, label: t("contact.accessLabel"), value: t("contact.accessText"), id: "acces" },
  ];

  const domaines = t("contact.domaines");
  const initial = { nom: "", prenom: "", societe: "", email: "", telephone: "", domaine: domaines[0], message: "", rgpd: false };

  const [form, setForm] = useState(initial);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.prenom || !form.email || !form.message) {
      setError(t("contact.errorRequired"));
      return;
    }
    if (!form.rgpd) {
      setError(t("contact.errorRgpd"));
      return;
    }
    setError("");
    setSending(true);
    try {
      const resp = await fetch(`/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.detail || "envoi impossible");
      }
      setSent(true);
    } catch {
      setError(t("contact.errorSend"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32 lg:py-40 bg-ink-soft" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="label-accent" data-testid="contact-overline">{t("contact.overline")}</span>
            <h2 className="mt-6 font-serif font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
              {t("contact.h2a")}
              <em className="italic text-gold-gradient not-italic">{t("contact.h2em")}</em>
            </h2>
            <p className="mt-6 text-sm sm:text-base text-[#A0A5B1] leading-relaxed max-w-md">
              {t("contact.intro")}
            </p>

            <div className="mt-12 space-y-6">
              {info.map((item) => (
                <div key={item.id} className="flex items-start gap-4" data-testid={`contact-info-${item.id}`}>
                  <span className="w-10 h-10 border border-gold/25 flex items-center justify-center shrink-0">
                    <item.icon className="w-4 h-4 text-gold" />
                  </span>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#9BA1B0] font-mono">{item.label}</p>
                    <p className="mt-1 text-sm text-[#FAF8F5]">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="bg-ink-card border border-white/5 p-8 sm:p-12"
          >
            <AnimatePresence mode="wait">
              {sent ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="h-full min-h-[420px] flex flex-col items-center justify-center text-center gap-6"
                  data-testid="contact-success"
                >
                  <CheckCircle2 className="w-14 h-14 text-gold" strokeWidth={1.2} />
                  <h3 className="font-serif text-2xl sm:text-3xl">{t("contact.successTitle")}</h3>
                  <p className="text-sm text-[#A0A5B1] max-w-md leading-relaxed">
                    {t("contact.successBody")}
                  </p>
                  <a
                    href="tel:+3228806402"
                    data-testid="contact-success-urgence"
                    className="border border-gold/40 text-gold text-xs uppercase tracking-[0.2em] px-6 py-3.5 hover:bg-gold/10 transition-colors duration-300"
                  >
                    {t("contact.successUrgence")}
                  </a>
                  <p className="text-xs text-[#9BA1B0] max-w-md leading-relaxed" data-testid="contact-success-saisine">
                    {t("contact.successSaisine")}
                  </p>
                  <button
                    onClick={() => { setSent(false); setForm(initial); }}
                    data-testid="contact-new-request"
                    className="mt-4 border border-gold/40 text-gold text-xs uppercase tracking-[0.25em] px-6 py-3 hover:bg-gold/10 transition-colors duration-300"
                  >
                    {t("contact.successNew")}
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onSubmit={submit}
                  className="space-y-7"
                  data-testid="contact-form"
                  noValidate
                >
                  <div>
                    <label htmlFor="domaine" className="label-accent block mb-3">{t("contact.domaineLabel")}</label>
                    <select
                      id="domaine"
                      value={form.domaine}
                      onChange={set("domaine")}
                      data-testid="contact-domaine-select"
                      className={`${field} bg-ink-card cursor-pointer`}
                    >
                      {domaines.map((d) => (
                        <option key={d} className="bg-ink-card">{d}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                    <div>
                      <label htmlFor="nom" className="label-accent block mb-3">{t("contact.nom")}</label>
                      <input id="nom" value={form.nom} onChange={set("nom")} placeholder={t("contact.phNom")} className={field} data-testid="contact-nom-input" />
                    </div>
                    <div>
                      <label htmlFor="prenom" className="label-accent block mb-3">{t("contact.prenom")}</label>
                      <input id="prenom" value={form.prenom} onChange={set("prenom")} placeholder={t("contact.phPrenom")} className={field} data-testid="contact-prenom-input" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                    <div>
                      <label htmlFor="societe" className="label-accent block mb-3">{t("contact.societe")}</label>
                      <input id="societe" value={form.societe} onChange={set("societe")} placeholder={t("contact.phSociete")} className={field} data-testid="contact-societe-input" />
                    </div>
                    <div>
                      <label htmlFor="telephone" className="label-accent block mb-3">{t("contact.telephone")}</label>
                      <input id="telephone" type="tel" value={form.telephone} onChange={set("telephone")} placeholder={t("contact.phTel")} className={field} data-testid="contact-telephone-input" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="label-accent block mb-3">{t("contact.email")}</label>
                    <input id="email" type="email" value={form.email} onChange={set("email")} placeholder={t("contact.phEmail")} className={field} data-testid="contact-email-input" />
                  </div>

                  <div>
                    <label htmlFor="message" className="label-accent block mb-3">{t("contact.messageLabel")}</label>
                    <textarea
                      id="message"
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      placeholder={t("contact.messagePlaceholder")}
                      className={`${field} resize-none`}
                      data-testid="contact-message-textarea"
                    />
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="contact-rgpd-label">
                    <input
                      type="checkbox"
                      checked={form.rgpd}
                      onChange={set("rgpd")}
                      className="mt-1 accent-[#C5A059] w-4 h-4"
                      data-testid="contact-rgpd-checkbox"
                    />
                    <span className="text-xs text-[#9BA1B0] leading-relaxed group-hover:text-[#A0A5B1] transition-colors">
                      {t("contact.rgpd")}
                    </span>
                  </label>

                  {error && (
                    <p className="text-xs text-red-400 tracking-wide" data-testid="contact-error">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit-button"
                    className="w-full bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium py-4 hover:bg-gold-light transition-colors duration-300 disabled:opacity-50 disabled:cursor-wait"
                  >
                    {sending ? t("contact.sending") : t("contact.submit")}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
